/**
 * Kamera-Mathematik. Bewusst geteilt, weil der Präsentationsmodus auf beiden
 * Seiten (Präsentierender rechnet Viewport aus, Gast rechnet Kamera daraus
 * zurück) exakt dieselben Formeln braucht.
 */
export const MIN_ZOOM = 0.02;
export const MAX_ZOOM = 20;
export function clampZoom(zoom) {
    return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));
}
export function screenToBoard(screen, camera, size) {
    return {
        x: (screen.x - size.width / 2) / camera.zoom + camera.x,
        y: (screen.y - size.height / 2) / camera.zoom + camera.y,
    };
}
export function boardToScreen(board, camera, size) {
    return {
        x: (board.x - camera.x) * camera.zoom + size.width / 2,
        y: (board.y - camera.y) * camera.zoom + size.height / 2,
    };
}
/** Sichtbarer Ausschnitt in Board-Koordinaten. */
export function cameraToViewport(camera, size) {
    const w = size.width / camera.zoom;
    const h = size.height / camera.zoom;
    return { x: camera.x - w / 2, y: camera.y - h / 2, w, h };
}
/**
 * Kamera, die `rect` vollständig einpasst ("contain").
 *
 * Das ist die Gegenrichtung zu `cameraToViewport` und der Kern des
 * Präsentationsmodus: Gäste mit abweichendem Seitenverhältnis sehen so
 * garantiert alles, was der Präsentierende sieht — im Zweifel etwas mehr.
 *
 * `minZoom` ist parametrisierbar, weil die übliche Untergrenze beim Folgen
 * gerade nicht gelten darf: sie existiert, damit man sich beim manuellen
 * Navigieren nicht im Leeren verliert. Griffe sie hier, sähe ein Gast mit
 * kleinerem Fenster bei einem sehr weit gefassten Frame *weniger* als der
 * Präsentierende — genau das, was diese Funktion verhindern soll.
 */
export function viewportToCamera(rect, size, padding = 1, minZoom = MIN_ZOOM) {
    const raw = Math.min(size.width / rect.w, size.height / rect.h) / padding;
    const zoom = Math.min(MAX_ZOOM, Math.max(minZoom, raw));
    return { x: rect.x + rect.w / 2, y: rect.y + rect.h / 2, zoom };
}
export function rectsIntersect(a, b) {
    return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}
/** Rechteck um seinen Mittelpunkt herum vergrößern (Faktor 1.2 = 20 % Rand). */
export function expandRect(rect, factor) {
    const dw = (rect.w * factor - rect.w) / 2;
    const dh = (rect.h * factor - rect.h) / 2;
    return { x: rect.x - dw, y: rect.y - dh, w: rect.w + dw * 2, h: rect.h + dh * 2 };
}
/**
 * Das größte Rechteck mit dem Seitenverhältnis `ratio`, das mittig in `area`
 * Platz findet — vorher auf `fill` der Fläche eingeschrumpft.
 *
 * Steht hier und nicht zweimal im Board: Ein neu abgelegtes Medium und ein neu
 * angelegter Frame beantworten dieselbe Frage („wie groß, damit es mittig ins
 * Blickfeld passt?"), und sie sollen sie gleich beantworten.
 */
export function fitRatio(area, ratio, fill) {
    let w = area.w * fill;
    let h = w / ratio;
    const maxH = area.h * fill;
    if (h > maxH) {
        h = maxH;
        w = h * ratio;
    }
    return { x: area.x + area.w / 2 - w / 2, y: area.y + area.h / 2 - h / 2, w, h };
}
export function boundingRect(rects) {
    if (rects.length === 0)
        return null;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const r of rects) {
        minX = Math.min(minX, r.x);
        minY = Math.min(minY, r.y);
        maxX = Math.max(maxX, r.x + r.w);
        maxY = Math.max(maxY, r.y + r.h);
    }
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}
export const easeInOutCubic = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
/**
 * Kamera-Interpolation für animierte Fahrten.
 *
 * `zoom` wird logarithmisch interpoliert: linear interpoliert fühlt sich eine
 * weite Fahrt am Ende spürbar "bremsend" an, logarithmisch bleibt die
 * wahrgenommene Geschwindigkeit konstant.
 */
export function lerpCamera(from, to, t) {
    const e = easeInOutCubic(t);
    return {
        x: from.x + (to.x - from.x) * e,
        y: from.y + (to.y - from.y) * e,
        zoom: Math.exp(Math.log(from.zoom) + (Math.log(to.zoom) - Math.log(from.zoom)) * e),
    };
}
//# sourceMappingURL=geometry.js.map